"use client";

import { cn } from "@/lib/utils";
import type { HeroSection, HeroSlide } from "@/ui/yns-components/components/hero-section/hero-section";
import type { YnsThemeType } from "@/ui/yns-components/components/types";
import { YnsLink } from "@/ui/yns-link";
import Image from "next/image";
import { useState } from "react";

export const HeroSectionInner = ({
	slides,
	theme,
	size = "fixed-width",
}: { slides: HeroSlide[]; theme: YnsThemeType<typeof HeroSection>; size?: "full-width" | "fixed-width" }) => {
	const [currentSlide, setCurrentSlide] = useState(0);

	return (
		<div>
			{slides.map((data, idx) => (
				<section
					key={idx}
					className={cn(
						`rounded-lg overflow-hidden flex items-center absolute inset-0 transition-opacity duration-1000`,
						currentSlide === idx ? "opacity-100" : "opacity-0 pointer-events-none",
						data.boxAlignment === "left" && "justify-start",
						data.boxAlignment === "center" && "justify-center",
						data.boxAlignment === "right" && "justify-end",

						data.textAlignment === "left" && "text-left",
						data.textAlignment === "center" && "text-center",
						data.textAlignment === "right" && "text-right",
						size === "full-width" && "items-stretch rounded-none",
					)}
				>
					<div
						className={cn(
							"max-w-xl space-y-4 text-(--theme-primary-color) p-8 lg:p-16 z-10 relative flex flex-col items-end justify-center",
							data.textAlignment === "left" && "items-start",
							data.textAlignment === "center" && "items-center",
							data.textAlignment === "right" && "items-end",
						)}
						style={{ backgroundColor: theme.backgroundColor ?? undefined, color: theme.color ?? undefined }}
					>
						<h2 className="text-balance text-3xl font-bold tracking-tight md:text-4xl" data-yns-field="title">
							{data.title}
						</h2>
						<p data-yns-field="description" className="text-pretty">
							{data.description}
						</p>
						<YnsLink
							className={cn(
								"inline-flex h-10 items-center justify-center rounded-full px-6 font-medium text-(--theme-button-color) transition-colors",
								"focus:outline-hidden focus:ring-1 focus:ring-(--theme-button-color)",
								"bg-[var(--theme-hero-section-cta-background-color,#222)]",
								"text-[var(--theme-hero-section-cta-color,#EEE)]",
								"hover:bg-[var(--theme-hero-section-cta-hover-background-color,#555)]",
							)}
							style={{
								"--theme-hero-section-cta-background-color": theme.buttonBackgroundColor ?? undefined,
								"--theme-hero-section-cta-color": theme.buttonTextColor ?? undefined,
								"--theme-hero-section-cta-hover-background-color":
									theme.buttonHoverBackgroundColor ?? undefined,
							}}
							href={data.button.path}
						>
							{data.button.label}
						</YnsLink>
					</div>
					{data.image.src && (
						<Image
							alt={data.image.alt}
							loading="eager"
							priority={true}
							fetchPriority="high"
							className="rounded absolute top-0 left-0 object-cover w-full h-full"
							width={1280}
							height={720}
							src={data.image.src}
							style={{
								objectFit: "cover",
								zIndex: "-1",
							}}
							sizes="(max-width: 1280px) 100vw, 1280px"
						/>
					)}
				</section>
			))}

			{/* Indicators */}

			{slides.length > 1 && (
				<div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 items-center bg-neutral-700/50 backdrop-blur-sm rounded-full px-2 py-1">
					{slides.map((_, index) => (
						<button
							type="button"
							key={index}
							onClick={() => setCurrentSlide(index)}
							className={`bg-white rounded-full transition-[width,height] ${
								currentSlide === index ? "size-2" : "size-1"
							}`}
							aria-label={`Go to slide ${index + 1}`}
						/>
					))}
				</div>
			)}
		</div>
	);
};
